import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { InlineEdit } from "@/components/design/InlineEdit";
import { SearchableSelect } from "@/components/design/SearchableSelect";
import { DatePicker } from "@/components/design/DatePicker";
import type { PlanPerson, ProjectPackage, ProjectTask } from "./project-plan";

// Die Felder einer Aufgabe. Zweizeilig wie die Aufgabe im Ruhezustand: Titel
// oben, alles Weitere als Meta darunter. Vorher standen alle vier Felder in
// EINER Zeile, die dadurch über die halbe Seite lief - beim Aufklappen sprang
// die Liste auseinander und der Blick verlor die Zeile, die man gerade ändert.
//
// Gemeinsame Datumsauswahl, in beiden Planansichten mit unveränderter Bereichsordnung.

/**
 * Das Zuweisungsfeld, geteilt von Aufgabe und Paket. Als eigener Baustein, weil
 * beide dieselben zwei Regeln brauchen: nur wer im Projekt ist, steht zur Wahl,
 * und eine bestehende Zuweisung bleibt sichtbar, auch wenn die Person das
 * Projekt verlassen hat. Zweimal geschrieben hiesse, dass eine der beiden
 * Stellen die zweite Regel irgendwann verliert.
 */
export function ProjectAssigneeField({
  value,
  people,
  assignee,
  onChange,
  id,
}: {
  value: string | null;
  people: readonly PlanPerson[];
  /** Die aufgelöste aktuelle Zuweisung, auch wenn sie nicht mehr Mitglied ist. */
  assignee: PlanPerson | null;
  onChange: (next: string | null) => void;
  id?: string;
}) {
  const intl = useIntl();
  const unassigned = intl.formatMessage({ id: "project.lanes.unassigned", defaultMessage: "Ohne Zuweisung" });
  // Wer zugewiesen ist, steht im Feld - auch wenn die Person das Projekt
  // verlassen hat. Ohne diese Zeile zeigte das Feld "Ohne Zuweisung", obwohl eine
  // Zuweisung existiert, und die nächste Auswahl hätte sie stillschweigend
  // überschrieben.
  const options = [
    { value: "", label: unassigned },
    ...people.map((p) => ({ value: p.id, label: p.name })),
    ...(assignee && !people.some((p) => p.id === assignee.id)
      ? [{ value: assignee.id, label: assignee.name }]
      : []),
  ];

  return (
    <div className="w-[11rem] shrink-0">
      <SearchableSelect
        id={id}
        value={value ?? ""}
        onChange={(next) => onChange(next || null)}
        options={options}
        // Suchzeile erst, wenn Lesen länger dauert als Tippen.
        searchable={options.length > 8}
        placeholder={unassigned}
        ariaLabel={intl.formatMessage({ id: "project.plan.assignee", defaultMessage: "Zuständig" })}
      />
    </div>
  );
}


/**
 * Hält Beginn und Ende in der richtigen Reihenfolge. Wer den Beginn hinter das
 * Ende schiebt, meint fast immer "der ganze Zeitraum wandert" und nicht "das
 * Ding dauert minus drei Wochen" - also zieht das jeweils andere Datum mit,
 * statt eine Fehlermeldung zu zeigen, die man erst wegklicken muss.
 */
function orderedRange(patch: { start?: string; end?: string }, current: { start: string; end: string }) {
  const start = patch.start ?? current.start;
  const end = patch.end ?? current.end;
  if (!start || !end || start <= end) return patch;
  return patch.start !== undefined ? { start, end: start } : { start: end, end };
}

export function ProjectTaskFields({
  task,
  people,
  assignee,
  onChange,
  idPrefix,
}: {
  task: ProjectTask;
  people: readonly PlanPerson[];
  /** Die aufgelöste aktuelle Zuweisung, auch wenn sie nicht mehr Mitglied ist. */
  assignee: PlanPerson | null;
  onChange: (patch: Partial<ProjectTask>) => void;
  /** Testanker: `${idPrefix}-title` und `-assignee`. */
  idPrefix?: string;
}) {
  const intl = useIntl();

  return (
    <div className="min-w-0">
      <InlineEdit
        id={idPrefix && `${idPrefix}-title`}
        value={task.title}
        clearable={false}
        ariaLabel={intl.formatMessage({ id: "project.plan.taskTitle", defaultMessage: "Aufgabe" })}
        placeholder={intl.formatMessage({ id: "project.plan.unnamed", defaultMessage: "Ohne Namen" })}
        onChange={(value) => onChange({ title: value })}
      />
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <div className="w-[8.5rem] shrink-0">
          <DatePicker
            value={task.start}
            aria-label={intl.formatMessage({ id: "project.plan.start", defaultMessage: "Beginn" })}
            onChange={(start) => onChange(orderedRange({ start }, task))}
          />
        </div>
        <div className="w-[8.5rem] shrink-0">
          <DatePicker
            value={task.end}
            aria-label={intl.formatMessage({ id: "project.plan.end", defaultMessage: "Ende" })}
            onChange={(end) => onChange(orderedRange({ end }, task))}
          />
        </div>
        <ProjectAssigneeField
          id={idPrefix && `${idPrefix}-assignee`}
          value={task.assigneeId}
          people={people}
          assignee={assignee}
          onChange={(next) => onChange({ assigneeId: next })}
        />
      </div>
    </div>
  );
}

/**
 * Die Felder eines Pakets: Name, Zeitraum, Zuständigkeit. Dieselben vier
 * Eingaben wie bei einer Aufgabe, denn ein Paket IST ein Stück Arbeit - nur
 * eines, das weitere enthalten kann.
 *
 * Stand ausgeschrieben in der Projektfokus-Liste, bis der Personfokus dieselbe
 * Maske brauchte: dort klickt man einen Paketbalken an und will ihn ändern, nicht
 * erst in die andere Ansicht wechseln.
 */
export function ProjectPackageFields({
  pkg,
  people,
  assignee,
  onChange,
  onAddTask,
  idPrefix,
}: {
  pkg: ProjectPackage;
  people: readonly PlanPerson[];
  /** Die aufgelöste aktuelle Zuweisung, auch wenn sie nicht mehr Mitglied ist. */
  assignee: PlanPerson | null;
  onChange: (patch: Partial<ProjectPackage>) => void;
  /** Legt eine Aufgabe in diesem Paket an. Fehlt, wo nur bearbeitet wird. */
  onAddTask?: () => void;
  /** Testanker: `${idPrefix}-name`, `-start`, `-end`, `-assignee`, `-addtask`. */
  idPrefix?: string;
}) {
  const intl = useIntl();

  return (
    <div className="min-w-0">
      <InlineEdit
        id={idPrefix && `${idPrefix}-name`}
        value={pkg.name}
        clearable={false}
        inputClassName="font-medium"
        ariaLabel={intl.formatMessage({ id: "project.plan.packageTitle", defaultMessage: "Aufgabenpaket" })}
        placeholder={intl.formatMessage({ id: "project.plan.unnamed", defaultMessage: "Ohne Namen" })}
        onChange={(value) => onChange({ name: value })}
      />
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <div className="w-[8.5rem] shrink-0">
          <DatePicker
            id={idPrefix && `${idPrefix}-start`}
            value={pkg.start}
            aria-label={intl.formatMessage({ id: "project.plan.start", defaultMessage: "Beginn" })}
            onChange={(start) => onChange(orderedRange({ start }, pkg))}
          />
        </div>
        <div className="w-[8.5rem] shrink-0">
          <DatePicker
            id={idPrefix && `${idPrefix}-end`}
            value={pkg.end}
            aria-label={intl.formatMessage({ id: "project.plan.end", defaultMessage: "Ende" })}
            onChange={(end) => onChange(orderedRange({ end }, pkg))}
          />
        </div>
        <ProjectAssigneeField
          id={idPrefix && `${idPrefix}-assignee`}
          value={pkg.assigneeId}
          people={people}
          assignee={assignee}
          onChange={(next) => onChange({ assigneeId: next })}
        />
      </div>
      {/* Aufgabe anlegen gehört SICHTBAR in den offenen Kasten. Bisher gab es
          dafür nur ein Plus, das am Titel der geschlossenen Zeile erscheint,
          wenn man mit der Maus darüberfährt - wer gerade das Paket bearbeitet,
          findet es dort nicht, und mit der Tastatur schon gar nicht. */}
      {onAddTask ? (
        <div className="mt-2">
          <Button id={idPrefix && `${idPrefix}-addtask`} variant="secondary" size="sm" onClick={onAddTask}>
            <FormattedMessage id="project.plan.addTask" defaultMessage="Aufgabe hinzufügen" />
          </Button>
        </div>
      ) : null}
    </div>
  );
}
